import { ImageName } from "@mcleod/images";
import { Image, Label, Overlay, Panel, Snackbar, Toast } from "@mcleod/components";
import { Api, HorizontalAlignment, ModelRow, StringUtil } from "@mcleod/core";
import { BrokerageTrackingActions } from "./BrokerageTrackingActions";

const LOAD_TRACK_VENDOR = "L";
const MACROPOINT_LITE_VENDOR = "M";
const FOURKITES_VENDOR = "F";
const PROJECT44_VENDOR = "P";

const EMAIL_VENDOR_IDS = new Set([MACROPOINT_LITE_VENDOR, FOURKITES_VENDOR, LOAD_TRACK_VENDOR]);
const EDIT_VENDOR_IDS = new Set([MACROPOINT_LITE_VENDOR, LOAD_TRACK_VENDOR, PROJECT44_VENDOR]);
const TRACKING_ICON_VENDOR_IDS = new Set([MACROPOINT_LITE_VENDOR, LOAD_TRACK_VENDOR, FOURKITES_VENDOR, PROJECT44_VENDOR]);

export class BrTrackingQuickInfoPanel extends Panel {
    private labelVendorNameHeading: Label = new Label({
        text: "Vendor Name",
        fontSize: "12px",
        color: "default.light"
    });

    private labelVendorNameValue: Label = new Label({
        fontSize: "14px",
        fontBold: true
    });

    private labelTrackStatusHeader: Label = new Label({
        text: "Tracking Status",
        fontSize: "12px",
        color: "default.light"
    });

    private labeTrackStatusValue: Label = new Label({
        fontSize: "14px",
        fontBold: true
    });

    private labelTrackDescHeader: Label = new Label({
        text: "Tracking Desc",
        fontSize: "12px",
        color: "default.light"
    });

    private labeTrackDescValue: Label = new Label({
        fontSize: "14px",
        fontBold: true
    });

    private trackingActionsPanel: Panel = new Panel({
        padding: 0,
        paddingBottom: 0,
        align: HorizontalAlignment.LEFT,
        fillRow: true
    });

    private imageEmail: Image = new Image({
        name: ImageName.envelopeRead,
        visible: true,
        rowBreak: false,
        height: 22,
        width: 22,
        marginRight: 5,
        tooltip: "Send tracking message to driver"
    });

    private imagePencil: Image = new Image({
        name: ImageName.pencil,
        visible: true,
        rowBreak: false,
        height: 22,
        width: 22,
        marginRight: 5,
        tooltip: "Edit tracking"
    });

    private imageVendorTrackingIcon: Image = new Image({
        name: ImageName.shipmentLocation,
        visible: true,
        rowBreak: false,
        height: 22,
        width: 22,
        tooltip: "",
        marginRight: 5,
    });

    private imageCancel: Image = new Image({
        name: ImageName.circleX,
        visible: true,
        height: 22,
        width: 22,
        marginLeft: 3,
        tooltip: "Cancel tracking"
    });

    private editTrackingSlideoutProps = {
        width: 1200,
        height: window.innerHeight,
        backgroundColor: "defaultBackground",
        top: 30,
        padding: 0,
        borderRadius: 4,
        borderWidth: 1,
        borderShadow: true,
        borderColor: "strokeSecondary"
    };

    constructor(data: ModelRow<any>) {
        super();
        const brokerageTrackingActions = new BrokerageTrackingActions();
        const isManifest = !StringUtil.isEmptyString(data?.get("planned_manifest_id"))
            || !StringUtil.isEmptyString(data?.get("manifest_id"));

        this.align = HorizontalAlignment.LEFT;

        this.labelVendorNameValue.text = data?.get("br_vendor", "--");
        this.labeTrackStatusValue.text = data?.get("br_full_track_status", "--");
        this.labeTrackDescValue.text = data?.get("br_track_status_desc", "--");

        this.imageEmail.setProps({
            onClick: () => brokerageTrackingActions
                .sendTrackingMessage(
                    data?.get("movement_id"),
                    data?.get("br_vendor"),
                    this.sendMessageOnClose,
                    this.sendMessageDoAfterMessageSent)
        });

        this.imagePencil.setProps({
            onClick: () => brokerageTrackingActions.editTracking(
                data?.get("movement_id"),
                data?.get("order_id"),
                this.editTrackingSlideoutProps,
                null,
                isManifest)
        });
        this.imageVendorTrackingIcon.tooltip = setToolTipDetails(data?.get("br_vendor"));
        this.imageVendorTrackingIcon.setProps({
            onClick: () =>
                Api.search("lme/powerbroker/tracking/brokerage-tracking-logo",
                    {
                        "vendor_id": data?.get("br_vendor"),
                        "movement_id": data?.get("movement_id"),
                        "order_id": data?.get("order_id")
                    }).then(response => {
                        if (response != null && response.data != null) {
                            const onClickUrl = response?.data?.[0]?.on_click_url;
                            if (!StringUtil.isEmptyString(onClickUrl)) {
                                window.open(onClickUrl, '_blank', 'noreferrer');
                            } else {
                                Snackbar.showSnackbar("Tracking details not found", { millisUntilDismissal: 2000 })
                            }
                        }
                    })
        });

        this.imageCancel.setProps({
            onClick: () => brokerageTrackingActions.cancelTracking(
                data?.get("movement_id"),
                data?.get("br_vendor"))
        });

        const { brVendorId, isDfmTracking, whenFailedTo, whenFailedToAutomate, whenFailedToAutomateTracking, cancelBadge } = this.setFieldsForTrackingActionPanel(data);
        const showEmailAction = EMAIL_VENDOR_IDS.has(brVendorId);
        const showEditAction = EDIT_VENDOR_IDS.has(brVendorId);
        // DFM vendors are dynamic; use movement-level tracking mode to enable tracking URL action.
        const showTrackingIconAction = isDfmTracking || TRACKING_ICON_VENDOR_IDS.has(brVendorId);

        if (showEmailAction) {
            this.trackingActionsPanel.add(this.imageEmail);
        }

        if (showEditAction) {
            this.trackingActionsPanel.add(this.imagePencil);
        }

        if (showTrackingIconAction) {
            this.trackingActionsPanel.add(this.imageVendorTrackingIcon);
        }

        if (!whenFailedTo && !whenFailedToAutomate && !whenFailedToAutomateTracking && !cancelBadge) {
            this.trackingActionsPanel.add(this.imageCancel);
        }

        this.add(
            this.labelVendorNameHeading,
            this.labelVendorNameValue,
            new Label({ visible: true }), //spacer
            this.labelTrackStatusHeader,
            this.labeTrackStatusValue,
            new Label({ visible: true }), //spacer
            this.labelTrackDescHeader,
            this.labeTrackDescValue,
            new Label({ visible: true }), //spacer
            this.trackingActionsPanel
        );
    }

    private setFieldsForTrackingActionPanel(data: ModelRow<any>) {
        const brVendorId = data?.get("br_vendor_id");
        const isDfmTracking = data?.getBoolean("is_dfm_tracking", false);
        const brokerageTrackStatusBadge = data?.get("br_track_status_for_badge");
        const brokerageFullTrackStatus = data?.get("br_full_track_status");
        const whenFailedToAutomate: boolean = brokerageFullTrackStatus === "Failed to automate";
        const whenFailedToAutomateTracking: boolean = brokerageFullTrackStatus === "Failed to automate tracking";
        const whenFailedTo: boolean = brokerageTrackStatusBadge === "Failed To ...";
        const cancelBadge: boolean = brokerageTrackStatusBadge === "Cancelled";
        return { brVendorId, isDfmTracking, whenFailedTo, whenFailedToAutomate, whenFailedToAutomateTracking, cancelBadge };
    }

    private sendMessageOnClose(overLay: Overlay): void {
        Overlay.hideOverlay(overLay);
    }

    private sendMessageDoAfterMessageSent(overlay: Overlay, response: any): void {
        Overlay.hideOverlay(overlay);
        const message = response?.data?.[0]?.toast_message;
        if (message) {
            Toast.showSuccessToast(message);
        }
    }

}
function setToolTipDetails(vendorId: String) {
    if (!StringUtil.isEmptyString(vendorId)) {
        return "View load status on " + vendorId + " website."
    }
    return "";
}
